import { useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { ToggleRow } from '@/components/ui/ToggleRow';
import { Alert } from '@/components/ui/Alert';
import { GuildChannelSelect } from '@/components/dashboard/GuildChannelSelect';
import { SaveBar } from '@/components/ui/SaveBar';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useGuildConfig } from '@/hooks/useGuildConfig';

/** Real VALID_LOGS list from cogs/log_control.py, matching
 * bridge/config_registry.py's _VALID_LOG_TYPES exactly — the Bridge
 * rejects any other key with a 422, so this list must stay in sync with
 * the bot's own (there is no live endpoint to fetch it from). */
const toggleableLogs = ['ban', 'unban', 'kick', 'warn', 'mute', 'unmute', 'ticket', 'security', 'antinuke', 'raid', 'spam', 'lockdown'] as const;

/** Real cogs/events.py log types — confirmed to have no toggle support
 * anywhere in v18, so they're shown for visibility only, never sent to
 * the Bridge (which has no field for them either). */
const alwaysOnLogs = [
  { key: 'message_delete', label: 'Message deletes' },
  { key: 'message_edit', label: 'Message edits' },
  { key: 'member_update', label: 'Member updates' },
  { key: 'role_update', label: 'Role updates' },
  { key: 'voice', label: 'Voice state changes' },
];

interface LoggingData {
  mod_log: string | null;
  bot_log: string | null;
  types: Record<(typeof toggleableLogs)[number], boolean>;
}

export function LogsPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const { state, current, dirty, saving, saveError, update, save, discard } = useGuildConfig<LoggingData>(guildId, 'logging');

  if (state.kind === 'loading') {
    return (
      <div className="max-w-3xl space-y-6">
        <Card><Skeleton className="h-20 w-full" /></Card>
        <Card><Skeleton className="h-64 w-full" /></Card>
      </div>
    );
  }

  if (state.kind === 'forbidden') {
    return <Alert tone="danger" title="You don't have permission to view this" description="Logging settings require Manage Server." />;
  }

  if (state.kind === 'unavailable' || !current) {
    return <Alert tone="danger" title="Couldn't load logging settings" description={state.kind === 'unavailable' ? state.message : 'Something went wrong.'} />;
  }

  return (
    <div className="max-w-3xl space-y-6 pb-24">
      <div>
        <h1 className="text-h1 text-text-primary mb-1">Logs</h1>
        <p className="text-small text-text-secondary">Event logging configuration and search.</p>
      </div>

      {saveError && <Alert tone="danger" title="Couldn't save" description={saveError} />}

      <Card>
        <CardHeader title="Log channels" description="Where moderation and security events get posted." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-small text-text-secondary block mb-1">Mod log channel</label>
            <GuildChannelSelect
              guildId={guildId}
              value={current.mod_log}
              onChange={(id) => update({ mod_log: id })}
              aria-label="Mod log channel"
            />
          </div>
          <div>
            <label className="text-small text-text-secondary block mb-1">Bot log channel</label>
            <GuildChannelSelect
              guildId={guildId}
              value={current.bot_log}
              onChange={(id) => update({ bot_log: id })}
              aria-label="Bot log channel"
            />
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Toggleable log types" description="12 event types your server can turn on or off." />
        <div className="divide-y divide-border-subtle">
          {toggleableLogs.map((key) => (
            <ToggleRow
              key={key}
              title={key}
              checked={current.types?.[key] ?? true}
              onChange={(v) => update({ types: { ...current.types, [key]: v } })}
            />
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Always-on event logs"
          description="These 5 log types cannot be individually disabled by any command in v18 today — shown here for visibility, not configuration."
          action={<Badge tone="management">Always on</Badge>}
        />
        <div className="divide-y divide-border-subtle">
          {alwaysOnLogs.map((log) => (
            <div key={log.key} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
              <p className="text-body-medium text-text-primary">{log.label}</p>
              <span className="flex items-center gap-1.5 text-small text-text-tertiary">
                <Lock size={14} aria-hidden="true" />
                Always on
              </span>
            </div>
          ))}
        </div>
      </Card>

      <SaveBar visible={dirty || saving} onSave={save} onDiscard={discard} saving={saving} />
    </div>
  );
}
